/**
 * Corrections list — the read path for reported corrections.
 *
 * Non-admins see their own corrections; admins see all and can approve/reject
 * inline. Each row is self-contained: symptom, what the engine said, why it
 * was wrong, what was right, status, reporter, timestamp.
 *
 * Route: /learning/corrections
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw,
  AlertTriangle,
  GraduationCap,
  ArrowLeft,
  FileText,
  CheckCircle,
  XCircle,
  Search,
  X,
  WifiOff,
  Loader2,
  ChevronDown,
  ChevronUp,
  Inbox,
  CheckCheck,
  Trash2,
} from "lucide-react";
import AdminSideBar from "../AdminSideBar";
import UserProfileMenu from "../UserProfileMenu";
import SelectMenu from "../dashboard5/SelectMenu";
import { getStoredRole } from "../../utils/auth";
import { listCorrections, updateCorrection } from "./useLearning";
import LearningTabs from "./LearningTabs";

const AGENT_ROLES = new Set(["agent", "support_agent"]);

const TABS = [
  { key: "reported", label: "Reported" },
  { key: "published", label: "Published" },
  { key: "discarded", label: "Discarded" },
];

const EMPTY_COPY = {
  reported: {
    icon: Inbox,
    heading: "No corrections reported yet",
    body: "When engineers flag an answer as wrong from the chat, it will appear here for review.",
  },
  published: {
    icon: CheckCheck,
    heading: "No corrections published yet",
    body: "Approved corrections will appear here. They help steer the engine away from known-wrong answers.",
  },
  discarded: {
    icon: Trash2,
    heading: "No corrections discarded yet",
    body: "Rejected corrections will appear here for reference.",
  },
};

const STATUS_STYLES = {
  reported: "bg-amber-100 text-amber-800",
  published: "bg-emerald-100 text-emerald-800",
  discarded: "bg-red-50 text-red-600",
};

/** Clamp threshold — lines above this get a "Show more" toggle. */
const CLAMP_CHARS = 280;

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.reported;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${cls}`}
    >
      {status}
    </span>
  );
}

/** Renders a text block with clamp-and-expand when content exceeds CLAMP_CHARS. */
function ClampedText({ text, className }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const needsClamp = text.length > CLAMP_CHARS;
  const display = needsClamp && !expanded ? text.slice(0, CLAMP_CHARS) + "..." : text;
  return (
    <div className={className} style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
      <span>{display}</span>
      {needsClamp && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="ml-1.5 inline-flex items-center gap-0.5 text-[11px] font-medium text-cyan-600 hover:text-cyan-800 transition-colors"
          aria-label={expanded ? "Show less" : "Show more"}
        >
          {expanded ? (
            <>Show less <ChevronUp size={12} /></>
          ) : (
            <>Show more <ChevronDown size={12} /></>
          )}
        </button>
      )}
    </div>
  );
}

/** Confirmation dialog for approve/reject — renders inline beneath the row actions. */
function ConfirmAction({ action, onConfirm, onCancel, busy }) {
  const isApprove = action === "published";
  return (
    <div
      className={`mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-[12.5px] ${
        isApprove
          ? "bg-emerald-50 border border-emerald-200"
          : "bg-red-50 border border-red-200"
      }`}
    >
      <AlertTriangle
        size={14}
        className={isApprove ? "text-emerald-600 flex-shrink-0" : "text-red-500 flex-shrink-0"}
      />
      <span className={isApprove ? "text-emerald-800" : "text-red-700"}>
        {isApprove
          ? "Approve this correction? It will steer future answers."
          : "Reject this correction? The reporter's feedback will be discarded."}
      </span>
      <div className="ml-auto flex gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded px-2.5 py-1 text-[12px] font-medium text-gray-500 hover:text-gray-700 transition"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className={`rounded px-2.5 py-1 text-[12px] font-semibold text-white transition disabled:opacity-40 ${
            isApprove
              ? "bg-emerald-600 hover:bg-emerald-700"
              : "bg-red-500 hover:bg-red-600"
          }`}
        >
          {busy ? (
            <Loader2 size={12} className="animate-spin" />
          ) : isApprove ? (
            "Yes, approve"
          ) : (
            "Yes, reject"
          )}
        </button>
      </div>
    </div>
  );
}

const Corrections = ({ onLogout }) => {
  const navigate = useNavigate();
  const role = getStoredRole();
  const isAdmin = role === "admin";
  const isAgent = AGENT_ROLES.has(role);
  const [tab, setTab] = useState("reported");
  const [rows, setRows] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // True when the load failed due to a backend error (503) — visually distinct
  // from "no corrections yet" so an admin never confuses "broken" with "empty".
  const [loadFailed, setLoadFailed] = useState(false);
  // Track which rows have an in-flight action
  const [busyIds, setBusyIds] = useState(new Set());
  // Client-side filters over the rows already fetched for this tab.
  const [query, setQuery] = useState("");
  const [reporter, setReporter] = useState("");
  const [withAnswer, setWithAnswer] = useState(false);
  // Confirmation state: { id, action } or null
  const [confirmState, setConfirmState] = useState(null);
  // Toast feedback after action
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((msg, variant) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, variant });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setLoadFailed(false);
    try {
      const data = await listCorrections(tab);
      if (data === null) {
        setDisabled(true);
        setRows([]);
      } else {
        setDisabled(false);
        setRows(data.corrections || []);
      }
    } catch (e) {
      setLoadFailed(true);
      setError(e.message || "Could not load corrections.");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  /* ---- filters ---------------------------------------------------- */

  const clearFilters = useCallback(() => {
    setQuery("");
    setReporter("");
    setWithAnswer(false);
  }, []);

  // Never carry a filter across tabs — a reporter with rows in Reported may
  // have none in Published, which would read as "everything vanished".
  useEffect(() => {
    clearFilters();
  }, [tab, clearFilters]);

  const reporters = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => {
      if (r.reported_by) seen.add(r.reported_by);
    });
    return [...seen].sort();
  }, [rows]);

  const hasCorrectText = useMemo(
    () => rows.some((r) => !!r.correct_text),
    [rows]
  );

  const filtersActive = !!query.trim() || !!reporter || withAnswer;

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const haystack = [
          r.symptom_text,
          r.session_symptom,
          r.why_wrong,
          r.wrong_text,
          r.correct_text,
          r.reported_by,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (reporter && r.reported_by !== reporter) return false;
      if (withAnswer && !r.correct_text) return false;
      return true;
    });
  }, [rows, query, reporter, withAnswer]);

  const handleAction = async (id, action) => {
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await updateCorrection(id, { status: action });
      // Remove from list after a successful action
      setRows((prev) => prev.filter((r) => r.id !== id));
      setConfirmState(null);
      showToast(
        action === "published"
          ? "Correction approved and published."
          : "Correction rejected and discarded.",
        action === "published" ? "success" : "neutral"
      );
    } catch (e) {
      setError(e.message || `Failed to ${action} correction.`);
      setConfirmState(null);
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
      {isAgent ? (
        <aside className="flex h-screen w-[220px] flex-shrink-0 flex-col justify-between border-r border-[#EBECE6] bg-[#f6f6f1] py-4">
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => navigate("/TseDashboard5/new")}
              className="mx-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-gray-500 transition hover:bg-cyan-50 hover:text-cyan-700"
            >
              <ArrowLeft size={18} />
              <span className="whitespace-nowrap text-sm font-medium">
                Back to Copilot
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/learning")}
              className="mx-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-gray-500 transition hover:bg-cyan-50 hover:text-cyan-700"
            >
              <GraduationCap size={18} />
              <span className="whitespace-nowrap text-sm font-medium">
                Learning Trails
              </span>
            </button>
            <div className="mx-2 flex items-center gap-3 rounded-lg bg-cyan-500 px-3 py-2.5 text-white">
              <FileText size={18} />
              <span className="whitespace-nowrap text-sm font-medium">
                Corrections
              </span>
            </div>
          </div>
        </aside>
      ) : (
        <AdminSideBar onLogout={onLogout} />
      )}

      <div className="flex-1 relative flex flex-col px-6 sm:px-10 lg:px-16 xl:px-24 py-8 overflow-y-auto overflow-x-hidden min-w-0">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>

        {/* Toast notification */}
        {toast && (
          <div
            className={`fixed top-5 left-1/2 -translate-x-1/2 z-[60] rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg transition-all ${
              toast.variant === "success"
                ? "bg-emerald-600 text-white"
                : "bg-gray-700 text-white"
            }`}
          >
            {toast.msg}
          </div>
        )}

        <div className="w-full min-w-0">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#253439]">Corrections</h1>
              <p className="text-gray-500 text-sm mt-1">
                {isAdmin
                  ? "All reported corrections. Approve valid ones to update the knowledge base."
                  : "Corrections you reported on engine answers."}
              </p>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          <LearningTabs />

          {error && !loadFailed && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {disabled ? (
            <div className="border border-[#EBECE6] rounded-xl bg-white shadow-sm px-6 py-12 text-center">
              <GraduationCap size={26} className="mx-auto mb-3 text-gray-300" />
              <p className="text-[#253439] font-semibold">
                The learning module is switched off
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Turn it on in{" "}
                <button
                  type="button"
                  onClick={() => navigate("/admin/settings")}
                  className="text-cyan-600 hover:underline font-medium"
                >
                  Settings
                </button>{" "}
                to start capturing corrections.
              </p>
            </div>
          ) : (
            <>
              <div className="flex gap-2 mb-4">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`px-3.5 py-1.5 rounded-full text-sm transition border ${
                      tab === t.key
                        ? "bg-[#253439] border-[#253439] text-white font-semibold"
                        : "bg-white border-[#EBECE6] text-gray-500 hover:border-gray-300 hover:text-[#253439]"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Search + filters — always mounted so typing isn't interrupted. */}
              {!loadFailed && rows.length > 0 && (
                <div className="flex flex-col gap-2 mb-4 min-w-0">
                  <div className="relative max-w-md">
                    <Search
                      size={14}
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-300"
                    />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search symptom or why it was wrong"
                      aria-label="Search corrections"
                      className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-8 text-[13px] text-gray-700 placeholder-gray-400 outline-none transition-colors focus:border-cyan-300"
                    />
                    {query && (
                      <button
                        type="button"
                        onClick={() => setQuery("")}
                        aria-label="Clear search"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 transition-colors hover:text-cyan-700"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                    {hasCorrectText && (
                      <button
                        type="button"
                        onClick={() => setWithAnswer((v) => !v)}
                        aria-pressed={withAnswer}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                          withAnswer
                            ? "bg-cyan-500 text-white"
                            : "bg-gray-100 text-gray-500 hover:bg-cyan-50 hover:text-cyan-700"
                        }`}
                      >
                        Has correct answer
                      </button>
                    )}
                    {filtersActive && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-gray-400 transition-colors hover:text-cyan-700"
                      >
                        <X size={11} />
                        Clear
                      </button>
                    )}
                    {/* Reporter only means something to an admin — everyone
                        else is looking at nothing but their own reports. */}
                    {isAdmin && reporters.length > 1 && (
                      <SelectMenu
                        value={reporter}
                        onChange={setReporter}
                        options={[
                          { value: "", label: "All reporters" },
                          ...reporters.map((p) => ({ value: p, label: p })),
                        ]}
                        ariaLabel="Filter by reporter"
                        className="ml-auto"
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="border border-[#EBECE6] rounded-xl overflow-hidden bg-white shadow-sm relative min-w-0">
                {/* Subtle overlay when reloading with existing rows visible */}
                {loading && rows.length > 0 && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-xl">
                    <Loader2 size={22} className="animate-spin text-cyan-500" />
                  </div>
                )}
                {loading && rows.length === 0 ? (
                  <div className="flex items-center justify-center py-12 text-gray-400">
                    <Loader2 size={22} className="animate-spin" />
                  </div>
                ) : loadFailed ? (
                  <div className="text-center py-12">
                    <WifiOff size={26} className="mx-auto mb-3 text-red-300" />
                    <p className="text-[#253439] font-semibold">
                      Could not load corrections
                    </p>
                    <p className="text-gray-500 text-sm mt-1 max-w-md mx-auto">
                      {error || "The server did not respond. This is usually temporary."}
                    </p>
                    <button
                      type="button"
                      onClick={load}
                      className="mt-4 inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
                    >
                      <RefreshCw size={14} />
                      Retry
                    </button>
                  </div>
                ) : rows.length === 0 ? (
                  <EmptyState tab={tab} />
                ) : visibleRows.length === 0 ? (
                  <div className="text-center py-12 text-sm">
                    <p className="text-gray-400">
                      No corrections match that search.
                    </p>
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="mt-2 text-cyan-600 hover:underline font-medium text-[13px]"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <div className="learning-corrections-list">
                    {visibleRows.map((r) => (
                      <CorrectionRow
                        key={r.id}
                        row={r}
                        isAdmin={isAdmin}
                        busy={busyIds.has(r.id)}
                        confirmState={confirmState}
                        onRequestAction={(action) =>
                          setConfirmState({ id: r.id, action })
                        }
                        onConfirmAction={() =>
                          handleAction(confirmState.id, confirmState.action)
                        }
                        onCancelAction={() => setConfirmState(null)}
                        onNavigateTrail={
                          r.trail_id
                            ? () => navigate(`/learning/${r.trail_id}`)
                            : null
                        }
                      />
                    ))}
                  </div>
                )}
              </div>

              {!loading && visibleRows.length > 0 && (
                <p className="text-gray-400 text-xs mt-3">
                  {filtersActive
                    ? `${visibleRows.length} of ${rows.length} correction${
                        rows.length === 1 ? "" : "s"
                      }`
                    : `${rows.length} correction${
                        rows.length === 1 ? "" : "s"
                      }`}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* -- empty state for each sub-tab ------------------------------------ */
function EmptyState({ tab }) {
  const cfg = EMPTY_COPY[tab] || EMPTY_COPY.reported;
  const Icon = cfg.icon;
  return (
    <div className="text-center py-14 px-6">
      <Icon size={28} className="mx-auto mb-3 text-gray-300" />
      <p className="text-[#253439] font-semibold text-sm">{cfg.heading}</p>
      <p className="text-gray-400 text-[13px] mt-1 max-w-sm mx-auto">
        {cfg.body}
      </p>
    </div>
  );
}

/* -- single correction row -------------------------------------------- */
function CorrectionRow({
  row,
  isAdmin,
  busy,
  confirmState,
  onRequestAction,
  onConfirmAction,
  onCancelAction,
  onNavigateTrail,
}) {
  const isConfirming = confirmState && confirmState.id === row.id;
  return (
    <div
      className="border-b border-[#EBECE6] last:border-b-0 px-5 py-4 hover:bg-[#FAFAF6] transition min-w-0"
      style={{ overflowWrap: "anywhere" }}
    >
      {/* header line */}
      <div className="flex items-center gap-3 mb-3 flex-wrap min-w-0">
        <StatusPill status={row.status} />
        {isAdmin && row.reported_by && (
          <span className="text-xs text-gray-400">
            by {row.reported_by}
          </span>
        )}
        <span className="text-xs text-gray-400">
          {row.created_at
            ? new Date(row.created_at).toLocaleDateString()
            : ""}
        </span>
        {onNavigateTrail && (
          <button
            type="button"
            onClick={onNavigateTrail}
            className="ml-auto text-xs text-cyan-600 hover:underline font-medium"
          >
            View trail
          </button>
        )}
      </div>

      {/* symptom — the user's original question */}
      {row.symptom_text && (
        <div className="mb-3 min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 block mb-0.5">
            Symptom
          </span>
          <ClampedText
            text={row.symptom_text}
            className="text-[13px] text-[#253439] font-medium"
          />
        </div>
      )}

      {/* what the engine said */}
      {row.wrong_text && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 mb-3 min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 block mb-0.5">
            Engine answer
          </span>
          <ClampedText
            text={row.wrong_text}
            className="text-[12.5px] text-gray-600"
          />
        </div>
      )}

      {/* why it was wrong */}
      {row.why_wrong && (
        <div className="rounded-md border border-rose-100 bg-rose-50/50 px-3 py-2 mb-3 min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-rose-400 block mb-0.5">
            Why it was wrong
          </span>
          <ClampedText
            text={row.why_wrong}
            className="text-[12.5px] text-rose-700"
          />
        </div>
      )}

      {/* what was right */}
      {row.correct_text && (
        <div className="rounded-md border border-emerald-100 bg-emerald-50/50 px-3 py-2 mb-3 min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-500 block mb-0.5">
            Correct answer
          </span>
          <ClampedText
            text={row.correct_text}
            className="text-[12.5px] text-emerald-700"
          />
        </div>
      )}

      {/* admin actions */}
      {isAdmin && row.status === "reported" && !isConfirming && (
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={() => onRequestAction("published")}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-emerald-700 transition disabled:opacity-40"
            aria-label="Approve this correction"
          >
            <CheckCircle size={14} />
            Approve
          </button>
          <button
            type="button"
            onClick={() => onRequestAction("discarded")}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-[12.5px] text-gray-500 hover:border-red-300 hover:text-red-600 transition disabled:opacity-40"
            aria-label="Reject this correction"
          >
            <XCircle size={14} />
            Reject
          </button>
        </div>
      )}

      {/* confirmation step */}
      {isConfirming && (
        <ConfirmAction
          action={confirmState.action}
          onConfirm={onConfirmAction}
          onCancel={onCancelAction}
          busy={busy}
        />
      )}
    </div>
  );
}

export default Corrections;
